import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APIResponse } from '@mommy/models/Comm.model';
import {
  CalendarEventInfo,
  CalendarEventType,
  CreateCalendarEventDto,
  PatchCalendarEventDto,
} from '@mommy/models/CalendarEventInfo.model';
import { OcrDocumentResult } from '@mommy/models/OcrDocumentResult.model';
import { LoginFirebaseWithCustomToken } from '@mommy/state/auth/auth.actions';
import { Store } from '@ngxs/store';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { Api } from '../api';
import { FirebaseUtilsService } from '../firebase-utils.service';

/** GET /calendar-events 查詢條件 */
export interface GetEventsQuery {
  from: string; // YYYY-MM-DD（必填）
  to: string; // YYYY-MM-DD（必填）
  event_type?: CalendarEventType;
  page?: number;
  page_size?: number;
}

/**
 * 提醒中心 REST 服務層：封裝後端 [APP API]醫療行事曆 / 醫療單據。
 * 走既有 Api（environment.BASE_URL），token 由全域 TokenInterceptor 自動帶上。
 * 後端回應 envelope 為 { statusCode, status, message, data }，統一取 data。
 */
@Injectable({
  providedIn: 'root',
})
export class ReminderService {
  private readonly EVENTS_ENDPOINT = 'calendar-events';
  private readonly OCR_ENDPOINT = 'medical-documents/ocr';
  private readonly OCR_IMAGE_PATH = 'reminder-ocr';

  constructor(
    private api: Api,
    private fbUtil: FirebaseUtilsService,
    private store: Store
  ) {}

  /** GET /calendar-events?from&to&event_type&page&page_size */
  async getEvents(query: GetEventsQuery): Promise<CalendarEventInfo[]> {
    let params = new HttpParams()
      .set('from', query.from)
      .set('to', query.to);
    if (query.event_type) {
      params = params.set('event_type', query.event_type);
    }
    if (query.page != null) {
      params = params.set('page', String(query.page));
    }
    if (query.page_size != null) {
      params = params.set('page_size', String(query.page_size));
    }

    const res: APIResponse = (await this.api
      .get(this.EVENTS_ENDPOINT, null, { params })
      .toPromise()) as any;
    return this.normalizeList(res?.data);
  }

  /** GET /calendar-events/{id} */
  async getEvent(eventId: number): Promise<CalendarEventInfo> {
    const res: APIResponse = (await this.api
      .get(`${this.EVENTS_ENDPOINT}/${eventId}`)
      .toPromise()) as any;
    return this.normalizeEvent(res?.data);
  }

  /** POST /calendar-events */
  async createEvent(dto: CreateCalendarEventDto): Promise<CalendarEventInfo> {
    const res: APIResponse = (await this.api
      .post(this.EVENTS_ENDPOINT, dto)
      .toPromise()) as any;
    return this.normalizeEvent(res?.data);
  }

  /** PATCH /calendar-events/{id} */
  async patchEvent(
    eventId: number,
    dto: PatchCalendarEventDto
  ): Promise<CalendarEventInfo> {
    const res: APIResponse = (await this.api
      .patch(`${this.EVENTS_ENDPOINT}/${eventId}`, dto)
      .toPromise()) as any;
    return this.normalizeEvent(res?.data);
  }

  /** DELETE /calendar-events/{id} */
  async deleteEvent(eventId: number): Promise<void> {
    await this.api.delete(`${this.EVENTS_ENDPOINT}/${eventId}`).toPromise();
  }

  // ====== OCR ======

  /**
   * 上傳單據影像到 Firebase Storage，回傳可公開存取的 URL（供 OCR 的 image_url）。
   * 後端 OCR 只吃 image_url，App 拍照得到 base64，故先走 Firebase（比照 chat）。
   */
  async uploadImage(base64DataUrl: string): Promise<string> {
    // Firebase Storage 規則要求已登入；session 還原後 currentUser 可能為 null → 先補登入避免 403
    await this.ensureFirebaseAuth();
    const pure = base64DataUrl.includes(',')
      ? base64DataUrl.split(',')[1]
      : base64DataUrl;
    return this.fbUtil.uploadPictureByBase64_with_promise(
      this.OCR_IMAGE_PATH,
      pure,
      'jpg'
    );
  }

  /** 確保 Firebase Auth 已登入（未登入時以 stored custom token 補登入） */
  private async ensureFirebaseAuth(): Promise<void> {
    try {
      if (firebase.auth().currentUser) {
        return;
      }
      await this.store.dispatch(new LoginFirebaseWithCustomToken()).toPromise();
    } catch (err) {
      console.warn('[Reminder] ensureFirebaseAuth failed', err);
    }
  }

  /** POST /medical-documents/ocr：影像 OCR，回傳結構化辨識結果（不寫 DB） */
  async recognizeOcr(imageUrl: string): Promise<OcrDocumentResult> {
    const res: APIResponse = (await this.api
      .post(this.OCR_ENDPOINT, { image_url: imageUrl })
      .toPromise()) as any;
    return res?.data as OcrDocumentResult;
  }

  // ====== 回應正規化（後端回應 schema 未文件化，於 service 邊界吸收差異）======

  /** 清單可能為純陣列或分頁物件（items/list/rows/data），統一轉成陣列 */
  private normalizeList(data: any): CalendarEventInfo[] {
    const arr = Array.isArray(data)
      ? data
      : data?.items ?? data?.list ?? data?.rows ?? data?.data ?? [];
    return (arr || []).map((raw: any) => this.normalizeEvent(raw));
  }

  /** id 欄位可能為 event_id 或 id，統一補上 event_id */
  private normalizeEvent(raw: any): CalendarEventInfo {
    if (!raw) {
      return raw;
    }
    return {
      ...raw,
      event_id: raw.event_id ?? raw.id,
    };
  }
}
